import React from 'react';
import { Badge, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Modal from '../Common/Modal/Modal';
import { HANDOFF_REASONS } from '../../config/aiConversationStatus';
import callOutcomes from '../../config/callOutcomes';

/**
 * What the simulation amounted to, once it is over.
 *
 * Every value comes from `buildRecap`, which reads the event feed the engine
 * already produced — so this closes nothing, writes nothing and asks the
 * server nothing. The colleague opens it, reads it and closes it.
 *
 * Engine words go through the same maps the lead page uses: a recap that said
 * `abandoned` would undo its own purpose.
 */
const Row = ({ label, children }) => (
  <tr>
    <td className="text-muted" style={{ width: '42%' }}>
      {label}
    </td>
    <td>{children}</td>
  </tr>
);

const Offers = ({ orders, colour }) => (
  <>
    {orders.map((order) => (
      <Badge
        key={order.id}
        color={order.resolved ? colour : 'warning'}
        className="mr-1 font-size-11"
      >
        {order.name}
      </Badge>
    ))}
  </>
);

// Why the causale the model chose is not on screen. Both cases are
// configuration findings, and they are different ones.
const REASON_FATE = {
  severalOffers:
    'Not recorded: a causale is kept only when a single offer closes',
  noListOnTheOffer: 'None: the offer carries no reasons to choose from',
};

const SimulatorRecap = ({ recap, isOpen, toggle }) => {
  const { t } = useTranslation();
  if (!recap) return null;

  const outcome = callOutcomes[recap.outcome];
  const handoff = HANDOFF_REASONS[recap.handoffReason];

  const title = outcome
    ? t(outcome.title)
    : recap.handoffReason
    ? t('Handed over to the call center')
    : t('Conversation closed');

  const told =
    (recap.positive && recap.positive.message) ||
    (recap.proposal && recap.proposal.message) ||
    (recap.negative && recap.negative.message) ||
    '';

  return (
    <Modal
      size="lg"
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      showClose
      scrollable={false}
    >
      <div className="modal-body">
        <Table borderless size="sm" className="mb-2">
          <tbody>
            <Row label={t('Messages exchanged')}>
              {recap.interactions.total}{' '}
              <span className="text-muted">
                ({recap.interactions.lead} {t('from the lead')},{' '}
                {recap.interactions.agent} {t('from the agent')})
              </span>
            </Row>
            <Row label={t('Cost')}>${recap.costUsd.toFixed(4)}</Row>

            {!!recap.positive && (
              <>
                <Row label={t('Offers it closed on')}>
                  {recap.positive.orders.length ? (
                    <Offers orders={recap.positive.orders} colour="success" />
                  ) : (
                    <span className="text-muted">{t('None')}</span>
                  )}
                </Row>
                {!!recap.positive.notClosed.length && (
                  // The gate refused these while others went through, and
                  // nothing else in the recap would say so.
                  <Row label={t('Offers the gate refused')}>
                    <Offers orders={recap.positive.notClosed} colour="danger" />
                  </Row>
                )}
                <Row label={t('Positive reason')}>
                  {recap.positive.reasonFate === 'recorded' &&
                  recap.positive.reason ? (
                    recap.positive.reason
                  ) : (
                    <span className="text-muted">
                      {t(REASON_FATE[recap.positive.reasonFate] || 'None')}
                    </span>
                  )}
                </Row>
              </>
            )}

            {!!recap.proposal && (
              <>
                {/* Not a sale. The offer is configured so that a person
                    confirms it, so the interest was recorded and the
                    conversation went to the call center. Showing this as a
                    closure would claim a sale that did not happen. */}
                <Row label={t('Offers it proposed, without closing')}>
                  <Offers orders={recap.proposal.orders} colour="info" />
                </Row>
                <Row label={t('Why it did not close')}>
                  <span className="text-muted">
                    {t('The offer needs a person to confirm it')}
                  </span>
                </Row>
              </>
            )}

            {!!recap.appointment && (
              <>
                <Row label={t('Appointment')}>
                  {recap.appointment.date} {recap.appointment.time}
                </Row>
                <Row label={t('Appointment reason')}>
                  {recap.appointment.reason || (
                    <span className="text-muted">{t('None')}</span>
                  )}
                </Row>
                <Row label={t('Who would take it')}>
                  <span className="text-muted">
                    {t('Nobody yet: it goes to the call center unassigned')}
                  </span>
                </Row>
                <Row label={t('Call documents it would have touched')}>
                  {recap.appointment.callsTouched}
                </Row>
              </>
            )}

            {!!recap.negative && (
              <Row label={t('Negative reason')}>
                {recap.negative.reason || (
                  <span className="text-muted">{t('None')}</span>
                )}
              </Row>
            )}

            {!!recap.discarded.length &&
              recap.discarded.map((call, index) => (
                // The engine had already closed the turn at an earlier
                // outcome, so this call never ran. On close_positive the
                // offers below were accepted and nothing recorded them: shown
                // outside the closure block because it can sit beside a sale,
                // a proposal or an appointment alike.
                // eslint-disable-next-line react/no-array-index-key
                <Row key={`${call.name}-${index}`} label={t('Call discarded')}>
                  {call.orders.length ? (
                    <Offers orders={call.orders} colour="danger" />
                  ) : (
                    <span className="text-danger">{call.name}</span>
                  )}
                  <div className="text-muted font-size-11 mt-1">
                    {t(
                      'Accepted by the lead and recorded nowhere: the turn had already closed on an earlier outcome.',
                    )}
                  </div>
                </Row>
              ))}

            {!!(recap.positive || recap.proposal || recap.negative) && (
              // The sentence the lead actually received, which the engine
              // substitutes when the model omits one.
              <Row label={t('What the lead was told')}>
                {told || <span className="text-muted">{t('None')}</span>}
              </Row>
            )}

            {!!handoff && <Row label={t('Handoff reason')}>{t(handoff)}</Row>}
          </tbody>
        </Table>

        <p className="text-muted font-size-12 mb-0">
          {t(
            'Nothing was written: this was a simulation, and the documents it would have created are listed in the panel beside the conversation.',
          )}
        </p>
      </div>
    </Modal>
  );
};

export default SimulatorRecap;
